/**
 * `paperLayoutMode` — THE one expression that decides what a `.wb-paper` mount is.
 *
 * `data-layout-mode` is not decoration. It is the only thing that tells the
 * `.wb-paper` element whether it is still the MANUSCRIPT PAPER (its own chrome IS
 * the sheet — `styles/workbench.css`) or merely the DESK a real A4 canvas sits
 * on, in which case the outline chrome collapses
 * (`.wb-paper[data-layout-mode="paginated"]`, `./paginatedCanvas.css`).
 *
 * ## Why this is a module and not an inline ternary
 *
 * It has been an inline ternary twice, and it drifted BOTH times — the same
 * defect, reported by the owner on two separate days about two different pairs of
 * surfaces:
 *
 *   1. **2026-07-29, edit vs read.** Only the EDIT branch stamped the attribute,
 *      as a hardcoded literal. Read fell through to the unstamped rule and kept
 *      `44px 52px` + a hairline border UNDER a real A4 canvas — 106px of chrome
 *      edit did not have. Measured live at 1920px: `.ela-read-canvas__pages` had
 *      `clientWidth 697` against `scrollWidth 793`. The sheet was CLIPPED, not
 *      merely small, because the natural-scale policy no longer lets a page shrink
 *      to fit its box. Fixed by deriving the mode ONCE inside `<DoczCanvas>`.
 *
 *   2. **2026-07-31, head vs past revision.** Verbatim: *"why only the HEAD
 *      version has `data-layout-mode="paginated"`?!"* The fix above unified the
 *      two surfaces `<DoczCanvas>` owns — and `<DoczCanvas>` is not the only thing
 *      that mounts a `.wb-paper`. `<ArticleWorkbench>`'s past-revision pane renders
 *      its OWN hand-written `<div className="wb-paper">` around `<VersionCanvas>`,
 *      in a sibling branch `<DoczCanvas>` never runs through. So it carried no
 *      attribute at all and got the continuous manuscript chrome under a paginated
 *      canvas: a reviewer comparing a revision against the head was comparing two
 *      differently-boxed sheets.
 *
 *      `versionCanvas.css` closed with the claim that the revision *"replaces the
 *      head canvas inside `.wb-paper`, so it inherits that pane's paginated sheet
 *      metrics unchanged … Nothing to declare for it here, and that absence is the
 *      feature."* That was false. It did not replace anything inside the stamped
 *      paper; it rendered beside it in its own unstamped one. A comment asserting
 *      an invariant nothing implements is how the second drift survived the first
 *      fix.
 *
 * The lesson both times is the same, and "derive it once **inside the component**"
 * was not enough — the component is not the only mount. So the derivation now
 * lives OUTSIDE every mount, and each one calls it. `grep -rn "paperLayoutMode"
 * src/` enumerates every `.wb-paper` in the app together with the reason it chose
 * its mode; a mount that does not appear in that list is the next instance of this
 * bug.
 *
 * ⛔ Never write the literal string `"paginated"` / `"continuous"` into a
 * `data-layout-mode` attribute. Call this.
 */

/** What a `.wb-paper` mount is: the sheet itself, or the desk under real pages. */
export type PaperLayoutMode = "paginated" | "continuous";

/**
 * Resolve the layout mode from the ONE fact that decides it: does this paper
 * contain a paginated canvas painter?
 *
 * `true` → the `.pl-page` canvases inside ARE the physical sheets, so the paper is
 * only their desk and its outline chrome collapses. `false` → nothing paints pages
 * here, so the paper IS the sheet and keeps its chrome.
 *
 * Note this is deliberately a boolean and not a union of surface names. Naming the
 * surfaces is what produced a per-surface special case each time; the question is
 * about the PAINTER, and every mount can answer it.
 */
export function paperLayoutMode(
  paintsPaginatedCanvas: boolean,
): PaperLayoutMode {
  return paintsPaginatedCanvas ? "paginated" : "continuous";
}
